/**
 * src/features/auth/ui/LoginCard.tsx
 *
 * LoginCard — orchestrates the sign-in screen: label, referral callout, a Primary block, a
 * divider, and a Secondary block, with an email-group Password/Magic-link sub-method switch
 * inside whichever block holds it. Which block is Primary is driven by
 * estimatix:last_login_method (42-UI-SPEC.md Composition & Behavior Contract §1) — see the
 * comment on `lastMethod` below for the read itself, and on `googleIsPrimary` /
 * `activeEmailMethod` for how the four-row table folds into two booleans.
 *
 * AUTH-09 / adjacency + strict-mode invariant: exactly ONE input[type="email"] may exist
 * in this tree at any time. e2e/auth-gate.spec.ts's page.locator('input[type="email"]')
 * runs under Playwright's strict mode, which THROWS on 2+ matches — this is a hard
 * contract, not a styling convenience. The email-group below renders Password OR
 * Magic-link via AnimatePresence mode="wait", never both mounted at once — see the
 * comment on that block for why "wait" (not the default "sync") is load-bearing here.
 *
 * Both the Google block and the email-group mount unconditionally and simultaneously —
 * no accordion, no "show other methods" click-to-reveal (42-RESEARCH.md Pitfall 3). Only
 * their TOP/BOTTOM order changes with the remembered method; the internals of each block
 * are unaffected by which side of the divider it's currently rendered on.
 *
 * AUTH-06 — Google callback-error return: better-auth's errorCallbackURL: '/' (42-01) lands
 * the browser back here with a query string on failure. This component reads ONLY a
 * presence bit off it (see `hasGoogleCallbackError` below) — the value itself is untrusted
 * input (STRIDE T-42-11) and is never stored, never passed anywhere, and never rendered.
 * That same read also doubles as the WR-02 rollback trigger for GoogleSignInButton's
 * optimistic estimatix:last_login_method write — see the comment on `hasGoogleCallbackError`
 * below.
 *
 * Phase 43 — third email sub-method 'request-reset' (RequestPasswordResetForm) and the
 * router-state email relay: after a successful /auth/reset, ResetPasswordPage navigates
 * here with `{ state: { email } }`. That address is read ONCE, synchronously, in a lazy
 * state initializer (`resetEmail` below) — same AUTH-09 reasoning as `lastMethod`: an
 * effect would paint the first frame wrong before correcting itself. Introducing
 * `useLocation` puts this component inside the Router context requirement — every render
 * call site in this file's test needs a MemoryRouter wrapper (test/features/auth/LoginCard.test.tsx).
 */

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleSignInButton } from './GoogleSignInButton';
import { MagicLinkForm } from './MagicLinkForm';
import { PasswordSignInForm } from './PasswordSignInForm';
import { RequestPasswordResetForm } from './RequestPasswordResetForm';
import { RefereeCallout } from '@/features/referral';
import Button from '@/shared/ui/button';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';
import {
  consumeGoogleRollbackCandidate,
  getLastLoginMethod,
  setLastLoginMethod,
  type LoginMethod,
} from '../lib/lastLoginMethod';

type EmailSubMethod = 'password' | 'magic-link' | 'request-reset';

export const LoginCard: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const reduced = useReducedMotion();
  const shortDuration = reduced ? 0 : 0.2;

  // AUTH-06 — read the query string ONCE, synchronously, in a lazy initializer, and reduce
  // it to a single boolean: is a non-empty `error` param present. Nothing else is kept.
  // Plain URLSearchParams over window.location.search, not a router hook: this component
  // has no other dependency on the router today, and pulling one in for a single read
  // would force a Router-context wrapper onto every test in this file for no other benefit
  // (42-RESEARCH.md § Assumptions Log A3 — cheaper on dependencies, equally correct here).
  // The URL itself is never rewritten after this read — doing so would be an unrequested
  // side effect and would make the behavior unreproducible across a page reload.
  //
  // WR-02: this is also the only place a LATE Google failure (the browser physically left
  // this page and came back via errorCallbackURL: '/') can still be observed, so it also
  // consumes the rollback candidate GoogleSignInButton captured right before its optimistic
  // write. When an error is present, restore that candidate — never just clear to "nothing
  // remembered", since the prior value may have been a real, different preference (e.g.
  // 'password') that must not be silently discarded on an attempt that never completed. When
  // no error is present (a fresh visit, or a successful callback), still consume-and-discard
  // any leftover candidate, so a stale one never rolls back an unrelated, later Google click.
  // This runs BEFORE `lastMethod` below reads localStorage, so a restore is reflected on this
  // very render too — not just on the next visit.
  const [hasGoogleCallbackError] = useState<boolean>(() => {
    const errorPresent = Boolean(new URLSearchParams(window.location.search).get('error'));
    const rollbackCandidate = consumeGoogleRollbackCandidate();
    if (errorPresent && rollbackCandidate !== undefined) {
      setLastLoginMethod(rollbackCandidate);
    }
    return errorPresent;
  });

  // AUTH-09 / ordering: read SYNCHRONOUSLY in the lazy state initializer, never in an
  // effect. An effect would render the first frame with the default order and reorder on
  // the second frame — a visible flash of the wrong order the moment the screen mounts.
  // A lazy initializer runs during the very first render, so the first painted frame is
  // already correct.
  const [lastMethod] = useState<LoginMethod | null>(() => getLastLoginMethod());

  // Phase 43 (43-UI-SPEC.md Surface 1, state 4): the address ResetPasswordPage relays via
  // `navigate('/', { state: { email } })` after a successful reset. Read once, synchronously,
  // same lazy-initializer reasoning as `lastMethod` above. Absent in the common cross-tab /
  // cross-device case — that is the expected, non-error state (43-CONTEXT.md Область 1-бис),
  // not a fallback for a defect.
  const [resetEmail] = useState<string | undefined>(() => {
    const state = location.state as { email?: unknown } | null;
    return typeof state?.email === 'string' && state.email.length > 0 ? state.email : undefined;
  });

  // Phase 43 Task 2 (PWD-05): the invalid-token page's "request a new reset email"
  // affordance navigates here with this flag instead of an email — same navigation-state
  // channel as `resetEmail` above, not a second one. Read once, same lazy-initializer
  // reasoning.
  const [openRequestReset] = useState<boolean>(() => {
    const state = location.state as { openRequestReset?: unknown } | null;
    return state?.openRequestReset === true;
  });

  // 42-UI-SPEC.md Composition & Behavior Contract §1 folds into two booleans: 'password'
  // and 'magic-link' promote the email-group to Primary with that sub-method active;
  // everything else — no key at all, 'google', or any garbage value — gives Google Primary
  // AND an active magic-link sub-method AT THE SAME TIME. That "at the same time" is not a
  // detail, it protects two independent regression paths: five LoginCard tests require the
  // email field visible with zero clicks in EVERY state (adjacency), and three
  // e2e/real-loop/ specs start with an empty localStorage and expect an answer from the
  // magic-link endpoint specifically, not the password one (42-RESEARCH.md Pitfall 2).
  //
  // Phase 43: a present `resetEmail` forces the password sub-method regardless of
  // `estimatix:last_login_method` — resetting a password implies the user wants to sign in
  // with it next (43-CONTEXT.md Область 1, state 4). `openRequestReset` takes priority over
  // both — it means the user just came from a dead reset link and asked to request a new
  // one, so the request-reset form must open directly, not the password form.
  const [activeEmailMethod, setActiveEmailMethod] = useState<EmailSubMethod>(
    openRequestReset
      ? 'request-reset'
      : resetEmail !== undefined || lastMethod === 'password'
        ? 'password'
        : 'magic-link',
  );
  const googleIsPrimary = lastMethod !== 'password' && lastMethod !== 'magic-link';

  const toggleEmailMethod = () => {
    // Navigation within the screen, not a successful sign-in — never writes
    // estimatix:last_login_method (that only happens on a confirmed successful sign-in,
    // 42-UI-SPEC.md Composition & Behavior Contract §2/§4).
    setActiveEmailMethod((current) => (current === 'password' ? 'magic-link' : 'password'));
  };

  // Phase 43 — Surface 0: switches to the reset-request form. Not a sign-in, so this never
  // writes estimatix:last_login_method (same principle as toggleEmailMethod above).
  const switchToRequestReset = () => setActiveEmailMethod('request-reset');
  // Phase 43 — the reset-request form's "back to sign in" link, and the invalid-token
  // page's "request a new reset email" affordance (Task 2), both return here.
  const switchToPassword = () => setActiveEmailMethod('password');

  const googleBlock = (
    <div className="flex flex-col gap-2">
      {/* WR-01: LoginCard does NOT own a second role="alert" node here. A callback error
          detected via hasGoogleCallbackError and a later click-triggered error both surface
          through GoogleSignInButton's own single `error` state — seeded from the prop below,
          then freely overwritten by its own handleClick — so at most one Google error banner
          (one live region) is ever mounted, no matter which combination of the two triggers
          fires. */}
      <GoogleSignInButton
        initialError={
          hasGoogleCallbackError
            ? t('auth.google.error', 'Google sign-in failed. Please try again.')
            : null
        }
      />
    </div>
  );

  const emailGroupBlock = (
    <div className="flex flex-col gap-2">
      {/* mode="wait" is load-bearing, not cosmetic: it guarantees the incoming branch
          does not mount until the outgoing branch has fully unmounted, so a second
          input[type="email"] never exists — even transiently, mid-animation. Swapping
          this for the default AnimatePresence (both branches mounted during the
          crossfade) would give Playwright's strict-mode locator two matches and break
          e2e/auth-gate.spec.ts + e2e/real-loop/*.spec.ts (42-RESEARCH.md Pitfall 3). */}
      <AnimatePresence mode="wait">
        {activeEmailMethod === 'password' ? (
          <motion.div
            key="password"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: shortDuration }}
          >
            <PasswordSignInForm onForgotPassword={switchToRequestReset} initialEmail={resetEmail} />
          </motion.div>
        ) : activeEmailMethod === 'magic-link' ? (
          <motion.div
            key="magic-link"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: shortDuration }}
          >
            <MagicLinkForm />
          </motion.div>
        ) : (
          <motion.div
            key="request-reset"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: shortDuration }}
          >
            <RequestPasswordResetForm onBack={switchToPassword} />
          </motion.div>
        )}
      </AnimatePresence>
      {/* Phase 43: the request-reset step has its own "back to sign in" link — the
          Password/Magic-link switcher must not render alongside it (43-UI-SPEC.md Surface 0). */}
      {activeEmailMethod !== 'request-reset' && (
        <Button variant="link" onClick={toggleEmailMethod} className="self-center">
          {activeEmailMethod === 'password'
            ? t('auth.method.use_magiclink', 'Email me a sign-in link instead')
            : t('auth.method.use_password', 'Sign in with password instead')}
        </Button>
      )}
    </div>
  );

  const divider = (
    <div className="flex items-center gap-3" aria-hidden="true">
      <div className="flex-1 border-t border-slate-200 dark:border-slate-700" />
      <span className="text-caption uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {t('auth.divider.or', 'or')}
      </span>
      <div className="flex-1 border-t border-slate-200 dark:border-slate-700" />
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-caption text-slate-500 dark:text-slate-400 uppercase tracking-wide text-center">
        {t('landing.form_label', 'Sign in or create account')}
      </p>
      {/* REFUI-03: RefereeCallout — inline banner (not modal), between the label and the
          first interactive block. Moved here from AuthFormCard.tsx (Phase 42). */}
      <RefereeCallout />
      {googleIsPrimary ? (
        <>
          {googleBlock}
          {divider}
          {emailGroupBlock}
        </>
      ) : (
        <>
          {emailGroupBlock}
          {divider}
          {googleBlock}
        </>
      )}
    </div>
  );
};
